import React, {
  useState,
  forwardRef,
  useRef,
  useEffect,
  type InputHTMLAttributes,
} from "react";
// @ts-expect-error - this will be resolved at build time
import { Box, Text, Icon, InputPrimitive } from "@xsolla/xui-primitives";
import {
  useResolvedTheme,
  useId,
  type ThemeOverrideProps,
} from "@xsolla/xui-core";
import {
  Eye,
  EyeOff,
  Remove,
  CheckCr,
  ExclamationMarkCr,
} from "@xsolla/xui-icons-base";

export interface InputPasswordProps
  extends
    Omit<InputHTMLAttributes<HTMLInputElement>, "size" | "onChange">,
    ThemeOverrideProps {
  /**
   * Add eye in input with see password function.
   */
  extraSee?: boolean;
  /**
   * Add function clear for input.
   */
  extraClear?: boolean;
  /**
   * Property for specifying the name of the control.
   */
  name?: string;
  /**
   * Property for displaying an error and highlighting the control as invalid.
   */
  error?: boolean;
  /**
   * Visible password in the field.
   */
  initialVisible?: boolean;
  /**
   * Add checkup function for input. Can use after change input value.
   */
  extraCheckup?: (pass: string) => boolean;
  /**
   * Property for specifying the value of the control.
   */
  value?: string;
  /**
   * Property for displaying an error message and highlighting the control as invalid.
   */
  errorMessage?: string;
  /**
   * Property for specifying the placeholder of the control.
   */
  placeholder?: string;
  /**
   * Property for changing the size of the input.
   */
  size?: "xl" | "lg" | "md" | "sm" | "xs";
  /**
   * Property for disabling the control and highlighting it as an inactive state.
   */
  disabled?: boolean;
  /**
   * Property for displaying a label above the input.
   */
  label?: string;
  /**
   * Property for displaying helper text below the input.
   */
  helperText?: string;
  /**
   * Event handler when the value changes (for controlled mode).
   */
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /**
   * Event handler when the text changes (alternative to onChange).
   */
  onChangeText?: (text: string) => void;
  /**
   * Function triggered when user clicks on extraClear button.
   */
  onRemove?: () => void;
  /**
   * Unique identifier for the input element. Used for accessibility linking.
   */
  id?: string;
  /**
   * Accessible label for screen readers when no visible label is present.
   */
  "aria-label"?: string;
  testID?: string;
}

export const InputPassword = forwardRef<HTMLInputElement, InputPasswordProps>(
  (
    {
      extraSee = false,
      extraClear = false,
      extraCheckup,
      initialVisible = false,
      value,
      placeholder,
      onChange,
      onChangeText,
      onKeyDown,
      onRemove,
      size = "md",
      disabled = false,
      name,
      label,
      helperText,
      errorMessage,
      error,
      id: providedId,
      "aria-label": ariaLabel,
      testID,
      themeMode,
      themeProductContext,
      ...rest
    },
    ref
  ) => {
    const { theme } = useResolvedTheme({ themeMode, themeProductContext });
    const [isVisible, setIsVisible] = useState(initialVisible);
    const [internalState, setInternalState] = useState<"default" | "focus">(
      "default"
    );
    const [passValue, setPassValue] = useState(value ?? "");
    const [isChecked, setIsChecked] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    const rawId = useId();
    const safeId = rawId.replace(/:/g, "");
    const inputId = providedId || `input-password-${safeId}`;
    const labelId = `${inputId}-label`;
    const errorId = `${inputId}-error`;
    const helperId = `${inputId}-helper`;

    React.useImperativeHandle(
      ref,
      () => inputRef.current as HTMLInputElement,
      []
    );

    useEffect(() => {
      if (value !== undefined) {
        setPassValue(value);
      }
    }, [value]);

    useEffect(() => {
      if (extraCheckup && passValue) {
        setIsChecked(extraCheckup(passValue));
      } else {
        setIsChecked(false);
      }
    }, [passValue, extraCheckup]);

    const isDisable = disabled;
    const isError = !!(errorMessage || error);
    const isFocus = internalState === "focus";

    const isCheckedShown = isChecked && !isError;
    const isExtraClearIconShown = !disabled && extraClear && !!passValue;
    const isExtraSeeShown = extraSee;
    const isErrorIconShown = isError;

    const extrasCount =
      Number(isExtraClearIconShown) +
      Number(isCheckedShown) +
      Number(isExtraSeeShown) +
      Number(isErrorIconShown);

    const sizeStyles = theme.sizing.input(size);
    const inputColors = theme.colors.control.input;

    const handleFocus = () => {
      if (!isDisable) {
        setInternalState("focus");
      }
    };

    const handleBlur = () => {
      if (!isDisable) {
        setInternalState("default");
      }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;

      if (onChange) {
        onChange(e);
      }
      if (onChangeText) {
        onChangeText(newValue);
      }

      setPassValue(newValue);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Escape") {
        e.currentTarget.blur();
      }
      if (onKeyDown) {
        onKeyDown(e);
      }
    };

    const handleClear = (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      setPassValue("");
      onRemove?.();

      if (inputRef.current) {
        if (typeof window !== "undefined" && window.HTMLInputElement) {
          const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
            window.HTMLInputElement.prototype,
            "value"
          )?.set;

          if (nativeInputValueSetter) {
            nativeInputValueSetter.call(inputRef.current, "");
          }
        }

        const syntheticEvent = {
          target: inputRef.current,
          currentTarget: inputRef.current,
          type: "change",
        } as React.ChangeEvent<HTMLInputElement>;

        onChange?.(syntheticEvent);
        inputRef.current.focus();
      }
    };

    const toggleVisibility = () => {
      setIsVisible(!isVisible);
    };

    let backgroundColor = inputColors.bg;
    let borderColor = inputColors.border;
    let outlineColor: string | undefined;

    if (isDisable) {
      backgroundColor = inputColors.bgDisable;
      borderColor = inputColors.borderDisable;
    } else if (isError) {
      outlineColor = theme.colors.border.alert;
      if (isFocus) {
        backgroundColor = theme.colors.control.focus.bg;
      }
    } else if (isFocus) {
      backgroundColor = theme.colors.control.focus.bg;
      outlineColor = theme.colors.border.brand;
    }

    const textColor = isDisable ? inputColors.textDisable : inputColors.text;
    const placeholderColor = inputColors.placeholder;
    const iconColor = inputColors.placeholder;

    // Padding values from Figma design
    const paddingConfig: Record<
      string,
      { vertical: number; horizontal: number }
    > = {
      xl: { vertical: 12, horizontal: 12 },
      lg: { vertical: 14, horizontal: 12 },
      md: { vertical: 11, horizontal: 12 },
      sm: { vertical: 7, horizontal: 10 },
      xs: { vertical: 7, horizontal: 10 },
    };

    // Icon sizes from Figma design
    const iconSizeConfig: Record<string, number> = {
      xl: 18,
      lg: 18,
      md: 18,
      sm: 16,
      xs: 16,
    };

    // Focus outline config from Figma design
    const focusOutlineConfig: Record<
      string,
      { width: number; offset: number }
    > = {
      xl: { width: 1, offset: -1 },
      lg: { width: 1, offset: -1 },
      md: { width: 1, offset: -1 },
      sm: { width: 1, offset: -1 },
      xs: { width: 1, offset: -1 },
    };

    const padding = paddingConfig[size];
    const borderRadius = theme.shape.input[size].borderRadius;
    const iconSize = iconSizeConfig[size];
    const focusOutline = focusOutlineConfig[size];

    return (
      <Box
        flexDirection="column"
        gap={sizeStyles.fieldGap}
        width="100%"
        testID={testID}
      >
        {label && (
          <Box as="label" id={labelId} htmlFor={inputId}>
            <Text
              color={theme.colors.content.secondary}
              fontSize={sizeStyles.fontSize - 2}
              fontWeight="500"
            >
              {label}
            </Text>
          </Box>
        )}
        <Box
          backgroundColor={backgroundColor}
          borderColor={borderColor}
          borderWidth={borderColor !== "transparent" ? 1 : 0}
          borderRadius={borderRadius}
          height={sizeStyles.height}
          paddingVertical={padding.vertical}
          paddingHorizontal={padding.horizontal}
          flexDirection="row"
          alignItems="center"
          gap={10}
          position="relative"
          style={{
            ...(outlineColor
              ? {
                  outline: `${focusOutline.width}px solid ${outlineColor}`,
                  outlineOffset: `${focusOutline.offset}px`,
                }
              : {}),
          }}
          hoverStyle={
            !isDisable && !isFocus && !isError
              ? {
                  backgroundColor: inputColors.bgHover,
                  borderColor: inputColors.borderHover,
                }
              : undefined
          }
        >
          <Box flex={1} height="100%" justifyContent="center">
            <InputPrimitive
              ref={inputRef}
              id={inputId}
              value={passValue}
              name={name}
              placeholder={placeholder}
              onChange={handleChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
              disabled={isDisable}
              type={isVisible ? "text" : "password"}
              color={textColor}
              fontSize={sizeStyles.fontSize}
              placeholderTextColor={placeholderColor}
              aria-invalid={isError || undefined}
              aria-describedby={
                errorMessage ? errorId : helperText ? helperId : undefined
              }
              aria-labelledby={label ? labelId : undefined}
              aria-label={!label ? ariaLabel : undefined}
              aria-disabled={isDisable || undefined}
              data-testid="input-password__field"
              {...rest}
            />
          </Box>

          {/* Right-side Extras Wrapper */}
          {extrasCount > 0 && (
            <Box flexDirection="row" alignItems="center" gap={4}>
              {isExtraClearIconShown && (
                <Box
                  as="button"
                  type="button"
                  alignItems="center"
                  justifyContent="center"
                  width={iconSize}
                  height="100%"
                  backgroundColor="transparent"
                  borderWidth={0}
                  cursor={disabled ? "not-allowed" : "pointer"}
                  onPress={!disabled ? handleClear : undefined}
                  disabled={disabled}
                  aria-label="Clear input"
                  data-testid="input-password__extra-clear-button"
                >
                  <Icon size={iconSize} color={textColor}>
                    <Remove />
                  </Icon>
                </Box>
              )}
              {isCheckedShown && (
                <Box
                  alignItems="center"
                  justifyContent="center"
                  width={iconSize}
                  height="100%"
                  role="img"
                  aria-label="Password meets requirements"
                  data-testid="input-password__check-icon"
                >
                  <Icon
                    size={iconSize}
                    color={theme.colors.content.success.primary}
                  >
                    <CheckCr />
                  </Icon>
                </Box>
              )}
              {isExtraSeeShown && (
                <Box
                  as="button"
                  type="button"
                  alignItems="center"
                  justifyContent="center"
                  backgroundColor="transparent"
                  borderWidth={0}
                  cursor="pointer"
                  onPress={toggleVisibility}
                  aria-label={isVisible ? "Hide password" : "Show password"}
                  aria-pressed={isVisible}
                  data-testid="input-password__visibility-toggle"
                >
                  {isVisible ? (
                    <Eye size={iconSize} color={iconColor} />
                  ) : (
                    <EyeOff size={iconSize} color={iconColor} />
                  )}
                </Box>
              )}
              {isErrorIconShown && (
                <Box
                  alignItems="center"
                  justifyContent="center"
                  width={iconSize}
                  height="100%"
                  role="img"
                  aria-label="Error"
                  data-testid="input-password__error-icon"
                >
                  <Icon
                    size={iconSize}
                    color={theme.colors.content.alert.primary}
                  >
                    <ExclamationMarkCr />
                  </Icon>
                </Box>
              )}
            </Box>
          )}
        </Box>

        {errorMessage && (
          <Text
            id={errorId}
            role="alert"
            color={theme.colors.content.alert.primary}
            fontSize={sizeStyles.fontSize - 2}
          >
            {errorMessage}
          </Text>
        )}

        {helperText && !errorMessage && (
          <Text
            id={helperId}
            color={theme.colors.content.secondary}
            fontSize={sizeStyles.fontSize - 2}
          >
            {helperText}
          </Text>
        )}
      </Box>
    );
  }
);

InputPassword.displayName = "InputPassword";
